import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserPlus, RefreshCw } from 'lucide-react';
import axios from 'axios';
import {
  listUsers,
  listInvitations,
  deactivateUser,
  reactivateUser,
  updateUserRole,
  revokeInvitation,
  resendInvitation,
} from '../api/users';
import type { UserListItem, InvitationListItem, RuoloAssegnabile } from '../api/users';

// R3-A: ruoli assegnabili dal selettore (5 attivi + OP; legacy OAP/COI esclusi).
const RUOLI_ASSEGNABILI: RuoloAssegnabile[] = ['admin', 'COMMERCIALE', 'AMM', 'DIR', 'RT', 'OP'];
import { useAuthStore } from '../store/authStore';
import InvitaUtenteModal from '../components/admin/InvitaUtenteModal';

type Tab = 'attivi' | 'inviti';

const STATUS_BADGE_CLASS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-700',
  accepted: 'bg-green-100 text-green-700',
  revoked: 'bg-stone-100 text-ink-soft',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function UtentiPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user: currentUser } = useAuthStore();
  const [tab, setTab] = useState<Tab>('attivi');
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [invitations, setInvitations] = useState<InvitationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser && currentUser.role !== 'admin') {
      navigate('/dashboard', { replace: true });
    }
  }, [currentUser, navigate]);

  const fetchData = useCallback(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [u, i] = await Promise.all([listUsers(), listInvitations()]);
        setUsers(u);
        setInvitations(i);
      } catch {
        setError(t('utenti.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function handleDeactivate(userId: string) {
    setActionError(null);
    try {
      await deactivateUser(userId);
      await fetchData();
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setActionError(err.response?.data?.detail ?? t('utenti.errore_disattivazione'));
      } else {
        setActionError(t('utenti.errore_disattivazione'));
      }
    }
  }

  async function handleReactivate(userId: string) {
    setActionError(null);
    try {
      await reactivateUser(userId);
      await fetchData();
    } catch {
      setActionError(t('utenti.errore_riattivazione'));
    }
  }

  async function handleRoleChange(userId: string, role: RuoloAssegnabile) {
    setActionError(null);
    try {
      await updateUserRole(userId, role);
      await fetchData();
    } catch (err: unknown) {
      // 422 ultimo-admin / 4xx self-guard → messaggio dal BE; ripristina il valore server nel select.
      if (axios.isAxiosError(err)) {
        setActionError(err.response?.data?.detail ?? t('utenti.errore_ruolo'));
      } else {
        setActionError(t('utenti.errore_ruolo'));
      }
      await fetchData();
    }
  }

  async function handleRevoke(invId: string) {
    setActionError(null);
    try {
      await revokeInvitation(invId);
      await fetchData();
    } catch {
      setActionError(t('utenti.errore_revoca'));
    }
  }

  async function handleResend(invId: string) {
    setActionError(null);
    try {
      await resendInvitation(invId);
      await fetchData();
    } catch {
      setActionError(t('utenti.errore_reinvio'));
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-ink-dark">{t('utenti.titolo')}</h1>
        <button
          type="button"
          onClick={() => setShowInviteModal(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors"
        >
          <UserPlus size={16} />
          {t('utenti.invita')}
        </button>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-stone-mid mb-4">
        {(['attivi', 'inviti'] as Tab[]).map((tk) => (
          <button
            key={tk}
            type="button"
            onClick={() => setTab(tk)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === tk
                ? 'border-sage text-sage'
                : 'border-transparent text-ink-soft hover:text-ink-dark'
            }`}
          >
            {tk === 'attivi' ? t('utenti.tab_attivi') : t('utenti.tab_inviti')}
          </button>
        ))}
      </div>

      {actionError && (
        <div className="mb-4 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
          {actionError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-ink-soft text-sm">
          <RefreshCw size={18} className="animate-spin mr-2" />
          {t('utenti.caricamento')}
        </div>
      ) : error ? (
        <div className="text-center py-12 text-accent-danger text-sm">{error}</div>
      ) : tab === 'attivi' ? (
        <UsersTable
          users={users}
          currentUserId={currentUser?.id ?? ''}
          onDeactivate={handleDeactivate}
          onReactivate={handleReactivate}
          onRoleChange={handleRoleChange}
        />
      ) : (
        <InvitationsTable
          invitations={invitations}
          onRevoke={handleRevoke}
          onResend={handleResend}
        />
      )}

      {showInviteModal && (
        <InvitaUtenteModal
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            setShowInviteModal(false);
            setTab('inviti');
            fetchData();
          }}
        />
      )}
    </div>
  );
}

function UsersTable({
  users,
  currentUserId,
  onDeactivate,
  onReactivate,
  onRoleChange,
}: {
  users: UserListItem[];
  currentUserId: string;
  onDeactivate: (id: string) => void;
  onReactivate: (id: string) => void;
  onRoleChange: (id: string, role: RuoloAssegnabile) => void;
}) {
  const { t } = useTranslation();
  if (users.length === 0) {
    return <p className="text-sm text-ink-soft text-center py-8">{t('utenti.nessun_utente')}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-ink-faint text-xs uppercase tracking-wide border-b border-stone-mid">
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_email')}</th>
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_nome')}</th>
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_ruolo')}</th>
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_stato')}</th>
            <th className="pb-2 font-semibold text-right">{t('utenti.col_azioni')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-mid">
          {users.map((u) => (
            <tr key={u.id} className="hover:bg-stone-bg/50 transition-colors">
              <td className="py-3 pr-4 text-ink-dark">{u.email}</td>
              <td className="py-3 pr-4 text-ink-mid">{u.full_name}</td>
              <td className="py-3 pr-4 text-ink-mid">
                <select
                  data-testid={`role-select-${u.id}`}
                  value={u.role}
                  disabled={u.id === currentUserId}
                  onChange={(e) => onRoleChange(u.id, e.target.value as RuoloAssegnabile)}
                  className="text-sm border border-stone-border rounded-md px-2 py-1 bg-white disabled:bg-stone-bg disabled:text-ink-soft disabled:cursor-not-allowed"
                  title={u.id === currentUserId ? t('utenti.ruolo_self_disabilitato') : undefined}
                >
                  {/* Ruolo corrente non nell'elenco assegnabile (es. legacy OAP/COI): opzione read-only. */}
                  {!RUOLI_ASSEGNABILI.includes(u.role as RuoloAssegnabile) && (
                    <option value={u.role}>{t(`common.ruoli.${u.role.toLowerCase()}`, { defaultValue: u.role })}</option>
                  )}
                  {RUOLI_ASSEGNABILI.map((r) => (
                    <option key={r} value={r}>{t(`common.ruoli.${r.toLowerCase()}`, { defaultValue: r })}</option>
                  ))}
                </select>
              </td>
              <td className="py-3 pr-4">
                {u.is_active ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    {t('utenti.stato_attivo')}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-ink-soft">
                    {t('utenti.stato_disattivato')}
                  </span>
                )}
              </td>
              <td className="py-3 text-right">
                {u.id !== currentUserId && (
                  u.is_active ? (
                    <button
                      type="button"
                      onClick={() => onDeactivate(u.id)}
                      className="text-xs px-3 py-1.5 border border-stone-border rounded-md text-ink-mid hover:bg-stone-bg transition-colors"
                    >
                      {t('utenti.disattiva')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onReactivate(u.id)}
                      className="text-xs px-3 py-1.5 border border-sage rounded-md text-sage hover:bg-sage/10 transition-colors"
                    >
                      {t('utenti.riattiva')}
                    </button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvitationsTable({
  invitations,
  onRevoke,
  onResend,
}: {
  invitations: InvitationListItem[];
  onRevoke: (id: string) => void;
  onResend: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (invitations.length === 0) {
    return <p className="text-sm text-ink-soft text-center py-8">{t('utenti.nessun_invito')}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-ink-faint text-xs uppercase tracking-wide border-b border-stone-mid">
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_email')}</th>
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_ruolo')}</th>
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_invitato_da')}</th>
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_data')}</th>
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_scadenza')}</th>
            <th className="pb-2 pr-4 font-semibold">{t('utenti.col_stato')}</th>
            <th className="pb-2 font-semibold text-right">{t('utenti.col_azioni')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-mid">
          {invitations.map((inv) => (
            <tr key={inv.id} className="hover:bg-stone-bg/50 transition-colors">
              <td className="py-3 pr-4 text-ink-dark">{inv.email}</td>
              <td className="py-3 pr-4 text-ink-mid">{t(`common.ruoli.${inv.role.toLowerCase()}`, { defaultValue: inv.role })}</td>
              <td className="py-3 pr-4 text-ink-mid">{inv.invited_by_name}</td>
              <td className="py-3 pr-4 text-ink-soft">{formatDate(inv.invited_at)}</td>
              <td className="py-3 pr-4 text-ink-soft">{formatDate(inv.expires_at)}</td>
              <td className="py-3 pr-4">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE_CLASS[inv.status] ?? ''}`}
                >
                  {t(`utenti.invito_stato.${inv.status}`, { defaultValue: inv.status })}
                </span>
              </td>
              <td className="py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  {(inv.status === 'pending' || inv.status === 'expired') && (
                    <button
                      type="button"
                      onClick={() => onResend(inv.id)}
                      className="text-xs px-3 py-1.5 border border-sage rounded-md text-sage hover:bg-sage/10 transition-colors"
                    >
                      {t('utenti.rinvia')}
                    </button>
                  )}
                  {inv.status === 'pending' && (
                    <button
                      type="button"
                      onClick={() => onRevoke(inv.id)}
                      className="text-xs px-3 py-1.5 border border-stone-border rounded-md text-ink-mid hover:bg-stone-bg transition-colors"
                    >
                      {t('utenti.revoca')}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
